import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme, useTheme } from '@mui/material/styles';
import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import Image from 'next/image';
import { Controller } from 'react-hook-form';
import ConnectUsemail from './ConnectUsEmail';
import { StFormLabel, StInputBase } from './ConnectUsForm.styles';
import ErrorIcon from './error_icon.svg';

export const connectUsFieldType = {
  DROPDOWN: 'Dropdown',
  TEXTBOX: 'TextBox',
};

type ConnectUsFormProps = {
  type: string;
  formElements: any;
  control: any;
  errors: any;
  getValues: any;
  name: string;
  key?: any;
};

const ConnectUsForm = ({
  type,
  formElements,
  control,
  errors,
  getValues,
  name,
}: ConnectUsFormProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(530));

  const getDropdownOptions = (name: string) => {
    switch (name) {
      case 'specialty':
        return formElements.fields?.specialities;
      case 'hcpPurpose':
        return formElements.fields?.resources;
      case 'suffix':
        return formElements.fields?.suffixes;
      default:
        return [];
    }
  };

  switch (type) {
    case connectUsFieldType.DROPDOWN:
      return (
        <>
          <Controller
            name={name}
            control={control}
            rules={{
              required: {
                value: formElements?.fields?.isRequired?.value,
                message: formElements?.fields?.errorMessage?.value,
              },
            }}
            defaultValue={''}
            render={({ field: { onChange, value, onBlur } }) => (
              <FormControl sx={{ width: isMobile ? '300px' : '450px', gap: '0.25rem' }}>
                <StFormLabel htmlFor={name}>
                  <Text field={formElements?.fields?.fieldLabel} />
                </StFormLabel>
                <Select
                  labelId={formElements?.fields?.fieldLabel?.value}
                  value={value || 'none'}
                  defaultValue={''}
                  onChange={onChange}
                  input={
                    <StInputBase
                      id={name}
                      sx={{
                        border: (theme: Theme) =>
                          errors[name] !== undefined
                            ? `1px solid ${theme.palette.error.main} !important`
                            : `1px solid ${theme.palette.neutral3.main}`,

                        borderRadius: '0.5rem',
                        '.MuiInputBase-input': {
                          padding: '16px 0 8px 0',
                        },
                        '.MuiSelect-select .MuiBox-root': {
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        },
                      }}
                      inputProps={{
                        onBlur: () => {
                          onBlur();
                        },
                      }}
                      startAdornment={
                        errors &&
                        errors[name] && (
                          <InputAdornment
                            position="start"
                            sx={{
                              minHeight: '15px',
                              minWidth: '15px',
                            }}
                          >
                            <Image src={ErrorIcon} width={15} height={15} />
                          </InputAdornment>
                        )
                      }
                      errors={errors[name] !== undefined ? true : false}
                    />
                  }
                >
                  <MenuItem value="none" disabled>
                    <Typography
                      sx={{
                        color: `${theme.palette.neutral6.main} !important`,
                        fontFamily: 'Proxima Nova',
                        fontSize: '16px',
                        lineHeight: '16px',
                      }}
                    >
                      <Text field={formElements?.fields?.placeholder} />
                    </Typography>
                  </MenuItem>
                  {getDropdownOptions(name)?.map((resource: any, index: number) => (
                    <MenuItem
                      key={index}
                      value={resource?.fields?.value?.value}
                      sx={{
                        fontFamily: 'Proxima Nova',
                        fontSize: '14px',
                        lineHeight: '24px',
                        fontWeight: 400,
                        color: `${theme.palette.black.main} !important`,
                        maxWidth: isMobile ? '300px' : '450px',
                      }}
                    >
                      <Box sx={{ whiteSpace: 'break-spaces' }}>
                        {resource?.fields?.value?.value || resource?.fields?.description?.value}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors[name] !== undefined && (
                  <Box
                    sx={{
                      fontSize: '12px',
                      fontFamily: 'Proxima Nova SemiBold',
                    }}
                  >
                    {errors[name].message || formElements?.fields?.regexErrorMessage?.value}
                  </Box>
                )}
              </FormControl>
            )}
          />
        </>
      );
    case connectUsFieldType.TEXTBOX:
      if (name === 'emailAddress') {
        return (
          <Controller
            control={control}
            name={name}
            defaultValue={''}
            rules={{
              required: {
                value: formElements?.fields?.isRequired?.value,
                message: formElements?.fields?.errorMessage?.value,
              },
              validate: () => {
                if (formElements?.fields?.regexValue && formElements?.fields?.regexValue?.value) {
                  const regEx = RegExp(formElements?.fields?.regexValue?.value);
                  const fieldValue = getValues(name);
                  return regEx.test(fieldValue)
                    ? true
                    : formElements?.fields?.regexErrorMessage?.value;
                }
              },
            }}
            render={({ field }) => (
              <ConnectUsemail
                emailDomains={formElements?.fields?.emailDomains}
                emailTitle={formElements?.fields?.fieldLabel}
                fieldProps={field}
                name={name}
                errors={errors}
                isMobile={isMobile}
                formElements={formElements}
              />
            )}
          />
        );
      } else {
        return (
          <Controller
            name={name}
            control={control}
            rules={{
              required: {
                value: formElements?.fields?.isRequired?.value,
                message: formElements?.fields?.errorMessage?.value,
              },
              validate: () => {
                if (formElements?.fields?.regexValue && formElements?.fields?.regexValue?.value) {
                  const regEx = RegExp(formElements?.fields?.regexValue?.value);
                  const fieldValue = getValues(name);
                  return regEx.test(fieldValue)
                    ? true
                    : formElements?.fields?.regexErrorMessage?.value;
                }
              },
            }}
            defaultValue={''}
            render={({ field: { onChange, value, onBlur } }) => (
              <>
                <FormControl sx={{ width: isMobile ? '300px' : '450px', gap: '0.25rem' }}>
                  <StFormLabel htmlFor={name}>
                    <Text field={formElements?.fields?.fieldLabel} />
                  </StFormLabel>
                  <StInputBase
                    id={name}
                    data-testid={name}
                    aria-describedby={formElements?.fields?.placeholder?.value}
                    value={value}
                    onChange={onChange}
                    placeholder={formElements?.fields?.placeholder?.value}
                    fullWidth
                    startAdornment={
                      errors &&
                      errors[name] && (
                        <InputAdornment
                          position="start"
                          sx={{
                            minHeight: '15px',
                            minWidth: '15px',
                          }}
                        >
                          <Image src={ErrorIcon} width={15} height={15} />
                        </InputAdornment>
                      )
                    }
                    sx={{
                      border: (theme: Theme) =>
                        errors[name] !== undefined
                          ? `1px solid ${theme.palette.error.main} !important`
                          : `1px solid ${theme.palette.neutral3.main}`,
                      borderRadius: '0.5rem',
                    }}
                    inputProps={{
                      maxLength: formElements?.fields?.maxLength?.value,
                      onBlur: () => {
                        onBlur();
                      },
                    }}
                    errors={errors[name] !== undefined ? true : false}
                  />
                  {errors[name] !== undefined && (
                    <Box
                      sx={{
                        fontSize: '12px',
                        fontFamily: 'Proxima Nova SemiBold',
                      }}
                    >
                      {errors[name]?.message || formElements?.fields?.regexErrorMessage?.value}
                    </Box>
                  )}
                </FormControl>
              </>
            )}
          />
        );
      }
    default:
      return <></>;
  }
};

export default ConnectUsForm;
